import * as React from 'react';
import { useState, useEffect } from 'react';
import { Field, Form, getFormValues, reduxForm, touch } from 'redux-form';
import {
  listPossibleLeaveReasons,
  listPossibleLeaveParticipants,
  getVacationBalance,
} from '../../redux/actions/table/timesheets';
import styles from '../../styles/forms/add_timesheet_wizard/AddTimeOffForm.scss';
import Button from '../../components/Button';
import ButtonGroup from '../../components/ButtonGroup';
import { withRouter } from 'react-router';
import { subscribe } from 'react-contextual';
import Localization from '../../helpers/Localization';
import { connect } from 'react-redux';
import { __ } from '../../core/utils';
import Select from '../../components/Select';
import Input from '../../components/Input';
import Textarea from '../../components/Textarea';
import Checkbox from '../../components/Checkbox';
import PeopleChooser from '../../components/PeopleChooser';
import DateTimePicker from '../../components/DateTimePicker';
import InfoPositiveIcon from '../../assets/images/info-positive-16x16.svg';

const AddTimeOffForm = (props) => {
  const VACATION_LEAVE_REASON_ID = 1;
  const TOIL_LEAVE_REASON_ID = 12;
  const [possible_leave_reasons, set_possible_leave_reasons] = useState([]);
  const [possible_leave_participants, set_possible_leave_participants] =
    useState([]);
  const [filteredParticipants, setFilteredParticipants] = useState([]);
  const [vacationBalance, setVacationBalance] = useState(null);

  useEffect(() => {
    listPossibleLeaveReasons().then(({ response }) => {
      set_possible_leave_reasons(response.data);
    });

    listPossibleLeaveParticipants().then(({ response }) => {
      set_possible_leave_participants(
        _.map(response.data, (row) => ({
          ...row,
          name: row.fullname,
        }))
      );
    });
  }, []);

  const { values, auth } = props;
  const selectedReasonId = Number(values?.reason) || 0;
  const showVacationInfo = selectedReasonId === VACATION_LEAVE_REASON_ID;
  const showToilInfo = selectedReasonId === TOIL_LEAVE_REASON_ID;
  const selectedUserId = values?.user_id || auth?.id;
  const selectedVacationDate = values?.all_day
    ? values?.date?.start
    : values?.date;

  const updateVacationBalance = (userId, date, reasonId) => {
    setVacationBalance(null);

    if (Number(reasonId) !== VACATION_LEAVE_REASON_ID || !userId || !date) {
      return;
    }

    getVacationBalance(userId, date)
      .then(({ response }) => {
        setVacationBalance(response.data);
      })
      .catch(() => {});
  };

  const possibleMinutes = [
    { value: '0', label: '00' },
    { value: 15, label: '15' },
    { value: 30, label: '30' },
    { value: 45, label: '45' },
  ];

  useEffect(() => {
    const currentUserId = auth?.id;
    const formUserId = values?.user_id;

    if (formUserId === currentUserId) {
      setFilteredParticipants(
        possible_leave_participants.filter((user) => user.id !== currentUserId)
      );
    } else {
      setFilteredParticipants(possible_leave_participants);
    }
  }, [values?.user_id, possible_leave_participants, auth?.id]);

  const selectedUser = _.find(
    possible_leave_participants,
    ['id', values?.user_id || auth?.id]
  );
  const availableCompensatoryDays =
    Number(selectedUser?.available_compensatory_days) || 0;
  const compensatoryDaysBase =
    Number(selectedUser?.compensatory_days_base) || 0;
  const vacationDaysRemaining =
    Number(
      vacationBalance?.vacation_days_remaining ??
        selectedUser?.vacation_days_remaining
    ) || 0;
  const vacationDaysBase =
    Number(
      vacationBalance?.vacation_days_base ?? selectedUser?.vacation_days_base
    ) || 0;

  const _handleSubmit = (formData) => {
    return props.onSubmit(formData);
  };

  return (
    <Form className={styles.wrapper} onSubmit={props.handleSubmit(_handleSubmit)}>
      <div className={styles.form}>
        <div className={styles.flex}>
          {_.includes(auth?.permissions, 'create.any.leave.request') && (
            <Field
              name='user_id'
              component={Select}
              searchable
              options={[
                ..._.map(possible_leave_participants, (row) => ({
                  value: row.id,
                  label: row.fullname,
                })),
              ]}
              label={__('table.columns.timesheet-users')}
              placeholder={__('add-timesheet-form.select-user')}
              onChange={(_event, userId) =>
                updateVacationBalance(
                  userId,
                  selectedVacationDate,
                  selectedReasonId
                )
              }
            />
          )}
        </div>
        <div className={styles.flex}>
          <Field
            name='all_day'
            component={Checkbox}
            label={__('add-timesheet-form.all_day')}
            className={styles.checkbox}
          />
        </div>

        {values?.all_day ? (
          <div>
            <Field
              name='date'
              component={DateTimePicker}
              label={__('table.columns.task-date-time')}
              footer
              range
              presets
              onChange={(_event, date) =>
                updateVacationBalance(
                  selectedUserId,
                  date?.start,
                  selectedReasonId
                )
              }
            />
          </div>
        ) : (
          <>
            <div className={styles.flex}>
              <div className={styles.f2}>
                <Field
                  name='date'
                  component={DateTimePicker}
                  label={__('table.columns.timesheet-date')}
                  footer
                  onChange={(_event, date) =>
                    updateVacationBalance(
                      selectedUserId,
                      date,
                      selectedReasonId
                    )
                  }
                />
              </div>
              <div className={styles.f2}>
                <Field
                  name='hours'
                  component={Input}
                  label={__('table.columns.timesheet-hours')}
                  numeric
                  maxLength={2}
                  hint={__('timesheets.hint.in-hours')}
                />
              </div>
              <div className={styles.f2}>
                <Field
                  name='minutes'
                  component={Select}
                  searchable
                  options={possibleMinutes}
                  label={__('table.columns.timesheet-minutes')}
                />
              </div>
            </div>
          </>
        )}

        <div className={styles.flex}>
          <div className={styles.f1}>
            <Field
              name='reason'
              component={Select}
              searchable
              options={_.map(possible_leave_reasons, (leaveReason) => ({
                value: leaveReason.id,
                label: leaveReason.requires_makeup
                  ? `${leaveReason.name} (${__('Requires makeup')})`
                  : leaveReason.name,
              }))}
              label={__('table.columns.timesheet-reason')}
              placeholder={__('add-timesheet-form.select-reason')}
              onChange={(_event, reasonId) =>
                updateVacationBalance(
                  selectedUserId,
                  selectedVacationDate,
                  reasonId
                )
              }
            />
            {(showVacationInfo || showToilInfo) && (
              <div className={styles.reasonInfo}>
                <InfoPositiveIcon className={styles.reasonInfoIcon} />
                <span className={styles.reasonInfoText}>
                  {showVacationInfo
                    ? `${__('Available vacation days:')} ${vacationDaysRemaining} d / ${vacationDaysBase} d`
                    : `${__('table.columns.available_compensatory_days')}: ${availableCompensatoryDays} d / ${compensatoryDaysBase} d`}
                </span>
              </div>
            )}
          </div>
        </div>
        <Field
          name='description'
          component={Textarea}
          label={__('add-timesheet-form.description')}
          maxLength={200}
          rows={1}
          maxRows={5}
          placeholder={__('add-timesheet-form.add-description')}
        />
        <div className={styles.flex}>
          <div className={styles.f1}>
            <Field
              name='approvers'
              component={PeopleChooser}
              label={__('table.columns.timesheet-approvers')}
              searchable
              options={filteredParticipants}
              placeholder={__('add-timesheet-form.select-approvers')}
            />
          </div>
          <div className={styles.f1}>
            <Field
              name='followers'
              component={PeopleChooser}
              label={__('table.columns.timesheet-followers')}
              searchable
              options={filteredParticipants}
              placeholder={__('add-timesheet-form.select-followers')}
            />
          </div>
        </div>

        <div className={styles.footer}>
          <Button
            lightGray
            medium
            left
            middleText={__('button.cancel')}
            onClick={props.actions.hideAddTimesheetWizard}
          />
          <ButtonGroup>
            <Button
              lightBlue
              medium
              middleText={__('button.create')}
              spinning={props.submitting}
              onClick={() => props.submit()}
            />
          </ButtonGroup>
        </div>
      </div>
    </Form>
  );
};

const mapStateToProps = (state) => {
  return {
    values: getFormValues('add_timesheet_wizard.time_off')(state),
    auth: state.auth,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    actions: {
      hideAddTimesheetWizard: () =>
        dispatch({
          type: 'table.timesheets/SET_TABLE_PARAMS',
          params: {
            wizard_timesheet_add: undefined,
            task_id: undefined,
          },
        }),
    },
  };
};

export default withRouter(
  reduxForm({
    form: 'add_timesheet_wizard.time_off',
    onSubmitFail: (_errors, dispatch) => {
      dispatch(touch('add_timesheet_wizard.time_off', 'date', 'minutes', 'reason'));
    },
  })(
    connect(
      mapStateToProps,
      mapDispatchToProps
    )(subscribe(Localization, 'localization')(AddTimeOffForm))
  )
);
